import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"

type PersonDetail = {
  id: number
  name: string
  slug: string
  content: string | null
  birth_date: string | null
  birth_date_precision: string | null
  birth_place: string | null
  death_date: string | null
  death_date_precision: string | null
  death_place: string | null
  death_age: number | null
  meta_title: string | null
  meta_description: string | null
  meta_keywords: string | null
  all_recordings_added: boolean
  in_amh: boolean
  amh_url: string | null
  members_count: number
  recordings_count: number
  gigs_count: number
  created_at: string
  updated_at: string
  added_by: { email: string }
}

export default function AdminPersonShow({ person }: { person: PersonDetail }) {
  function handleDelete() {
    if (confirm(`Delete "${person.name}"? This cannot be undone.`)) {
      router.delete(`/admin/people/${person.slug}`)
    }
  }

  return (
    <>
      <Head title={person.name}>
        <meta name="description" content={`Admin detail for ${person.name}.`} />
        <meta property="og:title" content={person.name} />
        <meta property="og:description" content={`Admin detail for ${person.name}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{person.name}</h1>
              <p className="mt-1">Person #{person.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="secondary">
                <Link href={`/admin/people/${person.slug}/edit`}>Edit</Link>
              </Button>
              <Button variant="danger" onClick={handleDelete}>Delete</Button>
              <Link href="/admin/people" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Back to people
              </Link>
            </div>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="Name" value={person.name} />
          <DetailRow label="Slug" value={person.slug} />
          <DetailRow label="Birth date" value={person.birth_date ?? "—"} />
          <DetailRow label="Birth place" value={person.birth_place ?? "—"} />
          <DetailRow label="Death date" value={person.death_date ?? "—"} />
          <DetailRow label="Death place" value={person.death_place ?? "—"} />
          <DetailRow label="Death age" value={person.death_age !== null ? String(person.death_age) : "—"} />
          <DetailRow label="In AMH" value={person.in_amh ? "Yes" : "No"} />
          <DetailRow label="AMH URL" value={person.amh_url ?? "—"} />
          <DetailRow label="All recordings added" value={person.all_recordings_added ? "Yes" : "No"} />
          <DetailRow label="Bands" value={String(person.members_count)} />
          <DetailRow label="Recordings" value={String(person.recordings_count)} />
          <DetailRow label="Gigs" value={String(person.gigs_count)} />
          <DetailRow label="Added by" value={person.added_by.email} />
          <DetailRow label="Created" value={formatDateTime(person.created_at)} />
          <DetailRow label="Updated" value={formatDateTime(person.updated_at)} />
        </dl>
      </AdminShell>
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
